import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Video, CalendarDays, ArrowUpRight, X, Check, UserX } from "lucide-react";

export const fmtTime = (s) => new Date(s).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function ShowUp({ lead, evId }) {
  const [val, setVal] = useState(lead.custom?.show_up || "");
  const [busy, setBusy] = useState(false);
  const save = async (v) => {
    const next = val === v ? "" : v;
    setBusy(true);
    try {
      await api.put(`/crm/leads/${lead._id}`, { custom: { show_up: next } });
      lead.custom = { ...(lead.custom || {}), show_up: next };
      setVal(next);
      window.dispatchEvent(new Event("cal-leads-changed"));
      toast.success(next ? `Show-up set to ${next}` : "Show-up cleared");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(false); }
  };
  const b = (v, Icon, on) => (
    <button disabled={busy} onClick={() => save(v)} data-testid={`event-showup-${v.toLowerCase()}-${evId}-${lead._id}`}
      className={`btn text-xs flex-1 justify-center transition-colors ${val === v ? on : ""}`}><Icon size={12} />{v === "Yes" ? "Showed up" : "No-show"}</button>
  );
  return (
    <div className="space-y-1" data-testid={`event-showup-${evId}-${lead._id}`}>
      <div className="text-[11px] text-neutral-500">Show-up {val ? <b className="text-neutral-300">{val}</b> : "not set"}</div>
      <div className="flex gap-2">
        {b("Yes", Check, "!bg-[#33b679] !border-[#33b679] !text-black")}
        {b("No", UserX, "!bg-[#d50000] !border-[#d50000] !text-white")}
      </div>
    </div>
  );
}

function LeadCard({ lead, evId, onOpen }) {
  const rows = [["Email", lead.email], ["Phone", lead.phone], ["Stage", lead.stage], ["Owner", lead.owner], ["Qualified", lead.qualification], ["Source", lead.utm_source || lead.source], ["Campaign", lead.utm_campaign], ["Company", lead.company]];
  return (
    <div className="space-y-2 pt-3 border-t border-[var(--border)]" data-testid={`lead-hover-${lead._id}`}>
      <div className="text-sm font-medium">{lead.name || lead.email || "Unnamed lead"}</div>
      <div className="grid grid-cols-[72px_1fr] gap-x-2 gap-y-1 text-xs">
        {rows.filter(([, v]) => v).map(([k, v]) => [<span key={k} className="text-neutral-500">{k}</span>, <span key={k + "v"} className="truncate">{String(v)}</span>])}
      </div>
      <ShowUp lead={lead} evId={evId} />
      <button onClick={onOpen} className="btn btn-primary text-xs w-full justify-center" data-testid={`event-go-lead-${evId}-${lead._id}`}>
        <ArrowUpRight size={12} /> Go to lead
      </button>
    </div>
  );
}

function Actions({ ev }) {
  const a = "btn text-xs flex-1 justify-center";
  return (
    <div className="flex gap-2">
      {ev.meet_url && <a href={ev.meet_url} target="_blank" rel="noreferrer" className={`${a} !bg-[#8ab4f8] !text-black !border-[#8ab4f8]`} data-testid={`event-meet-${ev.id}`}><Video size={12} /> Join Meet</a>}
      {ev.html_link && <a href={ev.html_link} target="_blank" rel="noreferrer" className={a} data-testid={`event-gcal-${ev.id}`}><CalendarDays size={12} /> See in Calendar</a>}
    </div>
  );
}

function EventWindow({ ev, leads, onClose }) {
  const nav = useNavigate();
  return (
    <div className="space-y-3" data-testid={`event-window-${ev.id}`}>
      <div className="flex items-start gap-2">
        <span className="w-3 h-3 rounded-sm mt-1 shrink-0" style={{ background: evColor(ev) }} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium leading-snug">{ev.title}</div>
          <div className="text-xs text-neutral-400 mt-0.5">
            {new Date(ev.all_day ? `${ev.start}T00:00:00` : ev.start).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}
            {!ev.all_day && ` · ${fmtTime(ev.start)} – ${fmtTime(ev.end)}`}
          </div>
        </div>
        {onClose && <button onClick={onClose} className="p-1 rounded hover:bg-[var(--panel-2)]" data-testid={`event-window-close-${ev.id}`}><X size={14} /></button>}
      </div>
      <Actions ev={ev} />
      {ev.attendees.length > 0 && <div className="text-[11px] text-neutral-500 break-words">{ev.attendees.map((x) => x.email).join(", ")}</div>}
      {ev.lead_ids.length === 0 && <div className="text-[11px] text-neutral-500" data-testid={`event-no-lead-${ev.id}`}>No lead matched these guests</div>}
      {ev.lead_ids.map((i) => <LeadCard key={i} lead={leads[i]} evId={ev.id} onOpen={() => nav(`/crm?lead=${i}`)} />)}
    </div>
  );
}

export function EventLink({ ev, leads, className, style, children }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const t = useRef(null);
  const enter = () => { clearTimeout(t.current); if (!open) t.current = setTimeout(() => setOpen(true), 250); };
  const leave = () => { clearTimeout(t.current); if (!pinned) t.current = setTimeout(() => setOpen(false), 120); };
  const change = (v) => { setOpen(v); if (!v) setPinned(false); };
  return (
    <Popover open={open} onOpenChange={change}>
      <PopoverTrigger asChild>
        <button type="button" className={className} style={style} data-testid={`event-${ev.id}`}
          onMouseEnter={enter} onMouseLeave={leave}
          onClick={(e) => { e.preventDefault(); clearTimeout(t.current); setOpen(true); setPinned(true); }}>{children}</button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" collisionPadding={12} onMouseEnter={() => clearTimeout(t.current)} onMouseLeave={leave}
        className="w-80 bg-[var(--panel)] border-[var(--border)] max-h-[70vh] overflow-y-auto">
        <EventWindow ev={ev} leads={leads} onClose={() => change(false)} />
      </PopoverContent>
    </Popover>
  );
}

export const evColor = (ev) => ev._color || (ev.lead_ids.length ? (ev.crm_status === "held" ? "#33b679" : ev.crm_status === "no_show" ? "#d50000" : "#039be5") : "#5f6368");
